<template>
  <div class="advisors">
    <section class="advisors__banner">
      <img
        src="/img/advisors-banner.jpg"
        alt="Advisors Banner"
        class="advisors__banner__image"
      />
      <div class="advisors__banner__overlay"></div>
      <div class="advisors__banner__content">
        <h1 class="advisors__banner__title">
          {{ $t("advisors.banner.title") }}
        </h1>
        <p class="advisors__banner__subtitle">
          {{ $t("advisors.banner.subtitle") }}
        </p>
      </div>
    </section>

    <section class="advisors__section wrapper">
      <h2 class="advisors__section__title">
        {{ $t("advisors.section.title") }}
      </h2>

      <div v-if="isLoading" class="advisors__loading">Loading advisors...</div>

      <div v-else-if="error" class="advisors__error">
        {{ error }}
      </div>

      <div v-else-if="advisors.length === 0" class="advisors__empty">
        No advisors available
      </div>

      <div v-else class="advisors__grid">
        <div
          v-for="advisor in advisors"
          :key="advisor.id"
          class="advisor__card"
        >
          <div class="advisor__image__wrapper">
            <img
              :src="advisor.photoUrl"
              :alt="advisor.name"
              class="advisor__image"
            />
          </div>

          <div class="advisor__info">
            <div class="advisor__name__wrapper">
              <h3 class="advisor__name">{{ advisor.name }}</h3>
              <img
                src="/img/check-mark.png"
                alt="Verified"
                class="advisor__checkmark"
              />
            </div>

            <p class="advisor__verified">
              {{ $t("advisors.section.verified") }}
            </p>

            <p class="advisor__description">
              {{
                locale === "es"
                  ? advisor.description_es
                  : advisor.description_en
              }}
            </p>

            <div class="advisor__tags__wrapper">
              <div class="advisor__tags">
                <span
                  v-for="(tag, index) in advisor.tags"
                  :key="index"
                  :class="[
                    'advisor__tag',
                    index === 0
                      ? 'advisor__tag--primary'
                      : 'advisor__tag--secondary',
                  ]"
                >
                  {{ locale === "es" ? tag.name_es : tag.name_en }}
                </span>
              </div>
            </div>

            <div class="advisor__button__wrapper">
              <a
                :href="getWhatsAppLink(advisor)"
                class="advisor__whatsapp"
                target="_blank"
                rel="noopener noreferrer"
              >
                {{
                  $t("advisors.section.talkNow", {
                    name: advisor.name.split(" ")[0],
                  })
                }}
                <i class="ri-whatsapp-line"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <Content />
    <Banner />
    <ContactBanner />
  </div>
</template>

<script setup>
import { onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useAdvisors } from "@/composables/useAdvisors";
import Content from "@/components/Content.vue";
import Banner from "@/components/banner/Banner.vue";
import ContactBanner from "@/components/banner/ContactBanner.vue";

const { locale } = useI18n();
const { advisors, isLoading, error, fetchAdvisorsForWebsite } = useAdvisors();

onMounted(() => {
  fetchAdvisorsForWebsite();
});

const getWhatsAppLink = (advisor) => {
  const phoneNumber = advisor.whatsapp
    ? advisor.whatsapp.replace(/\D/g, "")
    : "";
  const message = `Hello ${advisor.name}!`;
  return `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
};
</script>

<style scoped>
.advisors {
  width: 100%;
}

.advisors__banner {
  position: relative;
  width: 100%;
  min-height: 420px;
  overflow: hidden;
  border-radius: 0 0 48px 48px;
}

.advisors__banner__image {
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  top: 0;
  left: 0;
}

.advisors__banner__overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to bottom,
    rgba(19, 40, 48, 1) 0%,
    rgba(19, 40, 48, 0) 100%
  );
}

.advisors__banner__content {
  position: relative;
  z-index: 1;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 4rem 2rem 0;
  gap: 1rem;
}

.advisors__banner__title {
  color: #fff;
  font-size: 3.6rem;
  line-height: 1.2;
  font-weight: 700;
  max-width: 900px;
  margin: 0;
}

.advisors__banner__subtitle {
  color: #fff;
  font-size: 1.8rem;
  line-height: 1.4;
  font-weight: 400;
  margin: 0;
}

.advisors__section {
  padding: 6rem 0 !important;
}

.advisors__section__title {
  font-size: 2.8rem;
  font-weight: 700;
  color: #434856;
  margin-bottom: 4rem;
  text-align: left;
}

.advisors__grid {
  display: flex;
  flex-direction: column;
  gap: 4rem;
}

.advisor__card {
  display: flex;
  gap: 3rem;
  align-items: flex-start;
}

.advisor__image__wrapper {
  flex-shrink: 0;
  width: 320px;
  height: 320px;
  background-color: #fce519;
  border-radius: 20px;
  padding: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.advisor__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
}

.advisor__info {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.advisor__name__wrapper {
  display: flex;
  align-items: center;
  gap: 0;
}

.advisor__name {
  font-size: 2rem;
  font-weight: 700;
  color: #434856;
  margin: 0;
}

.advisor__checkmark {
  width: 28px;
  height: 28px;
  margin-left: 10px;
}

.advisor__verified {
  font-size: 1.5rem;
  color: #1b3b46;
  margin: 0;
  font-weight: 500;
}

.advisor__description {
  font-size: 1.6rem;
  color: #434856;
  line-height: 1.7;
  margin: 0;
}

.advisor__tags__wrapper {
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
}

.advisor__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
  justify-content: center;
}

.advisor__tag {
  display: inline-block;
  padding: 0.6rem 1.2rem;
  border-radius: 8px;
  font-size: 1.4rem;
  font-weight: 500;
}

.advisor__tag--primary {
  background-color: #fce519;
  color: #1b3b46;
}

.advisor__tag--secondary {
  background-color: #c4c4c4;
  color: #1b3b46;
}

.advisor__button__wrapper {
  display: flex;
  justify-content: center;
  margin-top: 1.5rem;
}

.advisor__whatsapp {
  display: inline-flex;
  align-items: center;
  gap: 0.8rem;
  padding: 1.2rem 1.8rem;
  background-color: #1b3b46;
  color: #fff;
  border-radius: 8px;
  font-size: 1.5rem;
  font-weight: 400;
  text-decoration: none;
  transition: background-color 0.3s ease;
  width: fit-content;
}

.advisor__whatsapp:hover {
  background-color: #0f2630;
}

.advisor__whatsapp i {
  font-size: 1.8rem;
}

.advisors__loading,
.advisors__error,
.advisors__empty {
  text-align: center;
  padding: 3rem;
  font-size: 1.6rem;
  color: #434856;
}

@media only screen and (max-width: 1024px) {
  .advisors__section {
    padding: 4rem 2rem !important;
  }
}

@media only screen and (max-width: 768px) {
  .advisors__banner {
    min-height: 320px;
    border-radius: 0 0 32px 32px;
  }

  .advisors__banner__content {
    padding: 3rem 1.5rem 0;
  }

  .advisors__banner__title {
    font-size: 2.4rem;
  }

  .advisors__banner__subtitle {
    font-size: 1.5rem;
  }

  .advisors__section {
    padding: 3rem 1.5rem !important;
  }

  .advisors__section__title {
    font-size: 2rem;
    margin-bottom: 2rem;
  }

  .advisors__grid {
    gap: 2rem;
  }

  .advisor__card {
    flex-direction: column;
    gap: 1.5rem;
    align-items: stretch;
  }

  .advisor__image__wrapper {
    width: 100%;
    height: auto;
    aspect-ratio: 1;
    border-radius: 16px;
  }

  .advisor__info {
    gap: 0.8rem;
  }

  .advisor__name {
    font-size: 1.7rem;
  }

  .advisor__checkmark {
    width: 24px;
    height: 24px;
    margin-left: 6px;
  }

  .advisor__verified {
    font-size: 1.3rem;
  }

  .advisor__description {
    font-size: 1.4rem;
    line-height: 1.6;
  }

  .advisor__tags {
    gap: 0.6rem;
  }

  .advisor__tag {
    padding: 0.5rem 1rem;
    font-size: 1.2rem;
  }

  .advisor__whatsapp {
    padding: 1rem 1.5rem;
    font-size: 1.3rem;
  }

  .advisor__whatsapp i {
    font-size: 1.6rem;
  }
}
</style>
